import { ClientError, ClientErrorCode } from '~/shared';

const screenshotError = (message: string) =>
	new ClientError(ClientErrorCode.HousesEditorError, {
		message,
		stage: 'clipboard',
	});

// Keep the image document alive BEFORE the copy click, so CEF grants that frame user activation.
export class HousesScreenshot {
	public readonly url: string;
	public readonly ready: Promise<void>;
	private readonly frame: HTMLIFrameElement;
	private nativeReady = false;
	private cancelLoading = () => {};

	constructor(private readonly blob: Blob) {
		this.url = URL.createObjectURL(blob);
		this.frame = document.createElement('iframe');
		this.frame.title = 'Снимок дома';
		this.frame.tabIndex = -1;
		this.frame.style.cssText =
			'position:fixed;left:-10000px;width:1px;height:1px;border:0';
		this.ready = new Promise<void>((resolve, reject) => {
			let settled = false;
			const timeout = setTimeout(
				() =>
					finish(
						screenshotError('Не удалось подготовить снимок к копированию.'),
					),
				5000,
			);
			const finish = (error?: Error) => {
				if (settled) return;
				settled = true;
				clearTimeout(timeout);
				this.frame.onload = null;
				this.frame.onerror = null;
				if (error) reject(error);
				else {
					this.nativeReady = true;
					resolve();
				}
			};
			this.cancelLoading = () => finish(screenshotError('Снимок сброшен.'));
			this.frame.onload = () => {
				const doc = this.frame.contentDocument;
				const image = doc?.querySelector('img');
				if (!doc?.contentType.startsWith('image/') || !image) return;
				const loaded = () => {
					if (image.naturalWidth > 0) finish();
				};
				if (image.complete) loaded();
				else image.addEventListener('load', loaded, { once: true });
			};
			this.frame.onerror = () =>
				finish(screenshotError('Не удалось открыть снимок.'));
			this.frame.src = this.url;
			document.body.append(this.frame);
		});
	}

	public get canCopy(): boolean {
		return (
			this.nativeReady ||
			(typeof navigator.clipboard?.write === 'function' &&
				typeof ClipboardItem !== 'undefined')
		);
	}

	public copy(): Promise<void> {
		if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
			return navigator.clipboard
				.write([new ClipboardItem({ 'image/png': this.blob })])
				.catch(() => this.copyNative());
		}
		// No await or navigation here: the native copy must run during the button gesture.
		this.copyNative();
		return Promise.resolve();
	}

	private copyNative(): void {
		if (!this.nativeReady)
			throw screenshotError('Снимок ещё не готов к копированию.');
		const focused = document.activeElement;
		try {
			this.frame.contentWindow?.focus();
			this.frame.contentWindow?.getSelection()?.removeAllRanges();
			if (!this.frame.contentDocument?.execCommand('copy'))
				throw screenshotError('Не удалось скопировать снимок.');
		} finally {
			if (focused instanceof HTMLElement) focused.focus();
		}
	}

	public dispose(): void {
		this.cancelLoading();
		this.nativeReady = false;
		this.frame.remove();
		URL.revokeObjectURL(this.url);
	}
}
